import { toast } from "@/components";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Switch,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { roles } from "@/lib/utils";
import { routes } from "@/routes/utils";
import {
  useDeleteEmployeeMutation,
  useGetDeactivationImpact,
  useGetEmployeeGrid,
  useUpdateEmployeeMutation,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical, Edit } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const useEmployee = () => {
  const queryClient = useQueryClient();
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [deletedRow] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showInActiveConfirmation, setShowInActiveConfirmation] =
    useState<boolean>(false);
  const [deactivationId, setDeactivationId] = useState<string>("");

  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const {
    data: employeeList,
    isLoading: isEmployeeLoading,
    refetch: refetchEmployeeList,
    isRefetching: isEmployeeRefetching,
  } = useGetEmployeeGrid({
    data: {
      search: globalFilter,
      searchFields: ["fullName", "email"],
      pageNumber,
      pageSize,
      filters: [
        {
          field: "userType",
          value: "EMPLOYEE",
          operator: "EQUALS",
        },
      ],
      sort: [
        {
          field: "createdAt",
          direction: "DESC",
        },
      ],
    },
  });

  useEffect(() => {
    const isSearching = globalFilter && globalFilter.length >= 2;
    const isCleared = globalFilter?.length === 0;
  
    if (isSearching || isCleared) {
      if (isSearching && (pageNumber !== 0 || pageSize !== 10)) {
         setPageNumber(0);
         setPageSize(10);
         return; 
      }
      refetchEmployeeList();
    }  
  }, [globalFilter, pageNumber, pageSize]);

  const { mutate: updateEmployee, isPending: isUpdateEmployeePending } =
    useUpdateEmployeeMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["employee-list"],
        });
        setShowInActiveConfirmation(false);
        toast({ message: "Status Updated Successfully" });
      },
    });

  const { data: deactivationImpact, isLoading: isDeactivationImpactLoading } =
    useGetDeactivationImpact({
      id: deactivationId,
    });

  const { mutate: deleteEmployee, isPending: isDeleteEmployeePending } =
    useDeleteEmployeeMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["employee-list"],
        });
        setShowInActiveConfirmation(false);
        setDeactivationId("");
        toast({ message: "Employee Deactivated Successfully" });
      },
    });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "userId",
      header: "ID",
      accessorFn: (row: any) => String(row.userId),
      size: 100,
    },
    {
      accessorKey: "fullName",
      header: "Employee Name",
      cell: ({ row }: any) => {
        return row.original.fullName ? (
          <span
            className="text-secondary border-b-1 border-secondary group-hover:border-secondary cursor-pointer font-[500]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`${row.original.userId}/view`);
            }}
          >
            {String(row.original.fullName)}
          </span>
        ) : (
          <div className="px-3">{""}</div>
        );
      },
      size: 200,
    },
    {
      accessorKey: "userType",
      header: <div className=" w-full">Role</div>,
      accessorFn: (row: any) =>
        String((roles as any)[row?.userType?.toLowerCase()]?.fullLabel || ""),
    },
    {
      accessorKey: "phone",
      header: <div className=" w-full">Contact Phone</div>,
      accessorFn: (row: any) => String(row?.phone || ""),
    },
    {
      accessorKey: "email",
      header: <div className=" w-full">Email ID</div>,
      accessorFn: (row: any) => String(row?.email || ""),
    },
    {
      accessorKey: "cityName",
      header: "City",
      accessorFn: (row: any) => String(row?.cityName || ""),
    },
    {
      accessorKey: "stateName",
      header: "State",
      accessorFn: (row: any) => String(row?.stateName || ""),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: any) => {
        return (
          <div>
            <Switch
              onCheckedChange={() => {
                setSelectedUser(row.original);
                if (row.original.status?.toLowerCase() === "active") {
                  setDeactivationId(String(row.original.userId));
                } else {
                  setDeactivationId("");
                }
                setTimeout(() => {
                  setShowInActiveConfirmation(true);
                }, 200);
              }}
              checked={row.original.status?.toLowerCase() == "active"}
            />
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`${row.row?.original?.userId}/edit`);
              }}
            >
              <Edit className="w-4 h-4" />
              Edit
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const onClose = () => {
    setOpenDialog(false);
  };

  const onAddEmployee = () => {
    navigate(`${routes.add}`);
  };

  const onActivateDeactivateEmployee = () => {
    if (selectedUser.status?.toLowerCase() === "active") {
      deleteEmployee({
        id: selectedUser.userId,
        acknowledgeImpact: deactivationImpact?.hasActiveAssignments,
      });
    } else {
      updateEmployee({
        id: selectedUser.userId,
        data: {
          ...selectedUser,
          status: "Active",
        },
      });
    }
  };

  return {
    onAddEmployee,
    globalFilter,
    setGlobalFilter,
    columns,
    onClose,
    openDialog,
    employeeList,
    isEmployeeLoading,
    deletedRow,
    isMobile,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    isEmployeeRefetching,
    showInActiveConfirmation,
    setShowInActiveConfirmation,
    selectedUser,
    onActivateDeactivateEmployee,
    isUpdateEmployeePending,
    deactivationImpact: deactivationImpact,
    isDeactivationImpactLoading,
    isDeleteEmployeePending,
    setDeactivationId,
  };
};

export default useEmployee;
